import * as Yup from 'yup';
import { setHours } from 'date-fns';

const lowerThanMessage = '${label} must be lower than or equal to ${max}';
const higherThanMessage = '${label} must be higher than or equal to ${min}';
const requiredFieldMessage = '${label} is a required field';

/* Creating Custom Methods
Yup.addMethod(Yup.type, ‘methodName’, function(anyArgsYouNeed) {
		const { message } = anyArgsYouNeed;
		return this.test(‘test-name’, message, function(value) {
				const { path, createError } = this;
				const { some, more, args } = anyArgsYouNeed;
				// [value] - value of the property being tested
				// [path]  - property name,
				// ...
				return someCondition || conditionTwo || createError(...);
		});
});
const schema = {
		name: Yup.string().required('Please enter your name!'),
		location: Yup.string()
				.matches(/regex/, 'Please enter the correct phone number!')
				.methodName(..., 'Custom validation failure message')
				.required('This field is required.'),
		company: ...,
}
*/

export const defaultValidations = () => {
  let schema = Yup.object({
    avalanche_program: Yup.object()
      .nullable()
      .test('program', 'Program is a required field', (value, context) => {
        if (context.parent.avalanche_program && context.parent.avalanche_program.value) return true;
        return false;
      }),
    avalanche_path: Yup.object()
      .label('Avalanche Path')
      .nullable()
      .test('Path', 'Avalanche Path is required', (value, context) => {
        if (context.parent.avalanche_path && context.parent.avalanche_path.value) return true;
        return false;
      }),
    avalanche_area: Yup.object()
      .label('Avalanche Area')
      .nullable()
      .test('AvalancheArea', 'Avalanche Area is required', (value, context) => {
        if (context.parent.avalanche_area && context.parent.avalanche_area.value) return true;
        return false;
      }),
    event_timestamp: Yup.date().nullable(),
    timestamp: Yup.date()
      .label('Occurrence Date/Time')
      .required(requiredFieldMessage)
      .test('futureDate', '${label} cannot be in the future', (value) => {
        return new Date(value) < new Date();
      })
      .test('minDateTime', '${label} cannot be earlier than parent Event date/time', (value, context) => {
        let et =
          context && context.parent && context.parent.uncertainty_hours
            ? new Date(new Date(value).setHours(new Date(value).getHours() + 1))
            : new Date(value);
        const parentTimestamp = context.parent.event_timestamp ? new Date(context.parent.event_timestamp) : et;
        return parentTimestamp <= et;
      }),

    observer: Yup.object().label('Observer').nullable().required(requiredFieldMessage),
    incident: Yup.boolean().label('Incident').required(requiredFieldMessage),
    comment: Yup.string().label('Comment').max(2000, '${ label } must not exceed 2000 characters'),
    /** aval_trig_type_cd & aval_mass_toe_dist are data driven validation*/
  });

  //custom test validations for time range +/- numeric string
  let yupObject = Yup.string();
  yupObject = yupObject.concat(yupObject.label('Time Range +/- hrs'));
  yupObject = yupObject.concat(yupObject.required(requiredFieldMessage));
  yupObject = lowerLimit(yupObject, 0);
  yupObject = upperLimit(yupObject, 250);
  yupObject = validateIncrement(yupObject, 1, 'F15', 'h');
  yupObject = decimalTest(yupObject, 1);

  schema = schema.concat(
    Yup.object({
      uncertainty_hours: yupObject,
    }),
  );

  return schema;
};

export const defaultControlValidations = () => {
  let schema = Yup.object({
    avalanche_program: Yup.object()
      .nullable()
      .test('program', 'Program is a required field', (value, context) => {
        if (context.parent.avalanche_program && context.parent.avalanche_program.value) return true;
        return false;
      }),
    avalanche_path: Yup.object()
      .label('Avalanche Path')
      .nullable()
      .test('Path', 'Avalanche Path is required', (value, context) => {
        if (context.parent.avalanche_path && context.parent.avalanche_path.value) return true;
        return false;
      }),
    avalanche_area: Yup.object()
      .label('Avalanche Area')
      .nullable()
      .test('AvalancheArea', 'Avalanche Area is required', (value, context) => {
        if (context.parent.avalanche_area && context.parent.avalanche_area.value) return true;
        return false;
      }),
    timestamp: Yup.date().label('Initiation Date/Time').nullable().required(requiredFieldMessage),
    blaster: Yup.object().label('Blaster').nullable().required(requiredFieldMessage),
    aval_cntrl_type_cd: Yup.object().label('Control Type').nullable().required(requiredFieldMessage),
    comment: Yup.string().label('Comment').max(2000, '${ label } must not exceed 2000 characters'),
  });

  return schema;
};

const lowerLimit = (yupObject, lowerLimit) => {
  yupObject = yupObject.concat(
    yupObject.test(
      'minValue',
      ({ label, min = lowerLimit }) => `${label} must be higher than or equal to ${min}`,
      (value) => {
        return value === undefined ? true : value >= parseFloat(lowerLimit);
      },
    ),
  );

  return yupObject;
};

const upperLimit = (yupObject, upperLimit) => {
  yupObject = yupObject.concat(
    yupObject.test(
      'maxValue',
      ({ label, max = upperLimit }) => `${label} must be lower than or equal to ${max}`,
      (value) => {
        return value === undefined ? true : value <= parseFloat(upperLimit);
      },
    ),
  );

  return yupObject;
};

const decimalTest = (yupObject, decimalPlaces) => {
  yupObject = yupObject.concat(
    yupObject.test(
      'decimalPlaces',
      `Please enter a value with ${decimalPlaces > 0 ? 'only ' + decimalPlaces : 'no'} decimal place`,
      (value) => {
        return ((value + '' || '').split('.')[1] || []).length <= decimalPlaces;
      },
    ),
  );
  return yupObject;
};

const validateIncrement = (yupObject, decimalPlaces, displayFormat, unitAbbreviation) => {
  // display format = F(ixed Point)1(Decimal Places)5(Increment * .1)
  // so F15 is a fixed point of 1 decimal place in 0.5 increments eg. 1.5

  //floating point math errors in javascript :()
  // use the determined number of decimal places
  // then 10 to the power of each decimal place as a multiplier
  // and use the multiplier to shift the decimal places
  const multiplier = Math.pow(10, decimalPlaces);
  //increment can't be zero or you get NaN in the test
  const incrementStr = displayFormat.substring(2) === '' ? 1 : displayFormat.substring(2);
  const increment = (displayFormat.startsWith('F') ? incrementStr : 0) / multiplier || 1;

  yupObject = yupObject.concat(
    yupObject.test(
      'validateIncrement',
      `Please enter a value in ${increment}${unitAbbreviation} increments`,
      (value) => {
        return Math.abs(((value || 0) * multiplier).toFixed()) % (increment * multiplier) === 0;
      },
    ),
  );

  return yupObject;
};

export const generateOptionalValidation = (eventProperties) => {
  let schema = Yup.object({});

  const validations = eventProperties.map((e) => {
    let yupObject =
      e.DataType === 'Numeric' ? Yup.string() : e.ObservationValues.length > 0 ? Yup.object() : Yup.string();

    yupObject = yupObject.concat(yupObject.label(e.DisplayName));
    if (e.IsMandatory === 'Y') {
      yupObject = yupObject.concat(yupObject.required(requiredFieldMessage));
    } else {
      yupObject = yupObject.concat(yupObject.nullable().notRequired());
    }

    if (e.LowerLimit?.length > 0) {
      yupObject = lowerLimit(yupObject, e.LowerLimit);
    }

    if (e.UpperLimit?.length > 0) {
      yupObject = upperLimit(yupObject, e.UpperLimit);
    }

    //we'll use the display format to handle the increments
    if (e.DisplayFormat.length > 0) {
      const decimalPlaces = e.DisplayFormat.startsWith('F') ? e.DisplayFormat.substring(1, 2) : 0;

      yupObject = validateIncrement(yupObject, decimalPlaces, e.DisplayFormat, e.UnitAbbreviation);

      if (e.DataType === 'Numeric') {
        //numeric fields need to test for decimal places based on data driven display format
        yupObject = decimalTest(yupObject, decimalPlaces);
      }
    }

    //special circumstances not db driven yet..
    const specialConditionControls = ['aval_rd_cover_length', 'aval_rd_dpth_max', 'aval_rd_dpth'];
    if (specialConditionControls.find((s) => s === e.ObservationName)) {
      yupObject = yupObject.concat(
        yupObject.when('aval_mass_toe_dist', {
          is: (val) => val > 0,
          then: yupObject.required('${label} is a required field'),
          otherwise: yupObject.notRequired(),
        }),
      );
    }

    if (e.ObservationName === 'aval_trig_dist') {
      yupObject = yupObject.concat(
        yupObject.when('aval_trig_type_cd', {
          is: (val) => {
            if (!val) return false;
            const trigSecondCode = val.value?.substr(-1);
            return val.value !== 'Nr' && (trigSecondCode == 'y' || trigSecondCode == 'r');
          },
          then: yupObject.required('${label} is a required field'),
          otherwise: yupObject.notRequired(),
        }),
      );
    }

    //WEAV-9357 - Avg Depth cannot be greater than Max Depth.
    if (e.ObservationName === 'aval_rd_dpth') {
      yupObject = yupObject.concat(
        yupObject.test('notGreaterThanMaxDepth', '${label} cannot be greater than Maximum Depth', (value, context) => {
          const maxDepth = parseFloat(context.parent.aval_rd_dpth_max || 0);
          const avgDepth = parseFloat(value || 0);
          return maxDepth >= avgDepth;
        }),
      );
    }

    return { item: e.ObservationName, schema: yupObject };
  });

  validations.map((v) => {
    schema = schema.concat(
      Yup.object({
        [v.item]: v.schema,
      }),
    );
  });

  return schema;
};
